import { CommanServices } from '@/services/commanServices';

// Shared helpers for external feed events (API Connections module).
// Used by the events modal on the API Connections page, the dashboard
// "External Events Watch" widget, and the map overlay.

// Report a normalized feed event as a REAL incident through the normal
// incident pipeline. `source` needs { name, category } (a connection or the
// event's embedded connection object).
export const reportFeedEvent = async (source, event) => {
  const fd = new FormData();
  const externalId = event.external_id || event.warning_id || event.id;
  const eventKey = event.event_key || event.canonical_event_key || event?.dedupe?.cluster_key;
  const evidenceTags = [
    'external',
    source?.category || 'feed',
    externalId ? `ext:${externalId}` : null,
    eventKey ? `foresight:${eventKey}` : null,
  ].filter(Boolean);
  fd.append('type_id', '');
  fd.append('title', event.title || 'External event');
  fd.append('description', event.description || '');
  fd.append('severity', event.severity || 'Medium');
  fd.append('notify_now', false);
  fd.append('is_exercise', false);
  fd.append('is_unknown_location', true); // external events carry GPS, not a mapped zone
  const gps = event.gps || event?.location?.gps;
  if (gps) fd.append('gps_data', JSON.stringify(gps));
  fd.append('tags', evidenceTags.join(','));
  fd.append('reporter_name', source?.name || 'External feed');
  return await CommanServices.addincidents(fd);
};

// "~120 km · near King Fahd Hospital" | "Far (2,400 km)" | "Unlocated"
export const distanceLabel = (event) => {
  if (event?.distance_km == null) return 'Unlocated';
  const km = Number(event.distance_km).toLocaleString();
  if (event.in_aoi) return `~${km} km · near ${event.nearest_zone || 'operations area'}`;
  return `Far (${km} km)`;
};

// Pre-written dispatch messages — the operator picks one, may edit it, then
// sends it to every active app user (mobile + web notification).
export const DISPATCH_TEMPLATES = [
  { id: 'alert', label: '🚨 Alert', build: (t) => `🚨 ALERT: ${t}. Please stay alert and await further instructions from the operations center.` },
  { id: 'evacuate', label: '🏃 Evacuation advisory', build: (t) => `🏃 EVACUATION ADVISORY: ${t}. Follow the evacuation procedures for your area and report to your assembly point.` },
  { id: 'standby', label: '🟡 Standby', build: (t) => `🟡 STANDBY: ${t}. Response teams should be ready to mobilize. Await activation orders.` },
  { id: 'info', label: 'ℹ️ Advisory', build: (t) => `ℹ️ ADVISORY: ${t}. Shared for your awareness — no action is required at this time.` },
];

// Normalize a stored early-warning doc (db.early_warnings) into the same
// shape the live watch events use, so one preview modal serves both.
export const warningToEvent = (w) => ({
  // Preserve the canonical ForeSight record first. The explicit fields below
  // keep compatibility with older stored warnings while retaining current
  // classification, operational-scope and evidence metadata for map previews.
  ...w,
  external_id: w.external_id,
  external_ids: w.external_ids || [w.external_id],
  event_key: w.canonical_event_key || w.event_key,
  warning_id: w.id,
  title: w.title,
  source_title: w.source_title || w.original_title || w.title,
  original_title: w.source_title || w.original_title || w.title,
  description: w.description,
  severity: w.severity,
  gps: w.gps,
  distance_km: w.distance_km,
  nearest_zone: w.nearest_zone,
  in_aoi: w.in_aoi ?? w.location?.in_aoi ?? false,
  location: w.location || null,
  location_resolution: w.location_resolution || w.location?.resolution || null,
  gps_derivation: w.gps_derivation || w.location?.gps_derivation || null,
  occurred_at: w.occurred_at || w.created_at,
  source_url: w.source_url,
  dispatched: !!w.dispatched,
  internal: !!w.internal,
  connection: {
    name: w.source_name,
    category: w.source_category,
    id: w.connection_id,
    source_url: w.source_url,
    external_id: w.external_id,
  },
  detecting_source: w.detecting_source || {
    name: w.source_name,
    category: w.source_category,
    connection_id: w.connection_id,
    source_url: w.source_url,
    source_title: w.source_title || w.original_title || w.title,
    external_id: w.external_id,
  },
  sources: w.sources || [],
  hazards: w.hazards || [],
  corroboration: w.corroboration || null,
  evidence: w.evidence || [],
  hazard_gate: w.hazard_gate || null,
  normalized_title: w.hazard_gate?.normalized_title || w.normalized_title,
  risk: w.risk || null,
  facts: w.facts || [],
  claims: w.claims || [],
  risk_zone: w.risk_zone || null,
  timing: {
    published_at: w.source_published_at || w.occurred_at,
    first_seen_at: w.platform_detected_at || w.first_seen_at || w.created_at,
    detection_latency_seconds: w.detection_latency_seconds ?? w.detection_latency?.seconds,
  },
});

// Ready-made connection templates for SuperConnect (one-click setup from the
// zero-events explainer). {OPS_*} placeholders are resolved by the backend.
// News monitors are GLOBAL by design. Like every other connected origin, an
// observation may open a hazard case or corroborate one already detected.
// Source type and credibility remain metadata in the case evidence trail.
const WITH_SELECTED_HAZARD_COVERAGE = (providerKey, hazardIds, template) => ({
  ...template,
  provider_key: providerKey,
  hazard_coverage: 'selected',
  hazard_ids: [...hazardIds],
  // Older ForeSight screens read effective_hazard_ids first. Carry the same
  // bounded allow-list so a template can never be mistaken for an all-hazard
  // source before the freshly-created connection is reloaded from the server.
  effective_hazard_ids: [...hazardIds],
});

const GDELT_TEMPLATE = (providerKey, label, query, hazardIds) =>
  WITH_SELECTED_HAZARD_COVERAGE(providerKey, hazardIds, {
    name: `News Monitor — ${label}`,
    category: 'news',
    base_url: `https://api.gdeltproject.org/api/v2/doc/doc?query=(${encodeURIComponent(query)})&mode=artlist&maxrecords=20&format=json&timespan=1d`,
    purposes: ['incident_reporting', 'notifications'],
    description: `Worldwide reporting matching ${label.toLowerCase()} terms. Observations are assessed against the Operations Area and may open or corroborate a unified ForeSight hazard case. Open API, no key (rate-limited).`,
  });

export const SUPER_CONNECT_TEMPLATES = {
  ukmto: WITH_SELECTED_HAZARD_COVERAGE('ukmto', ['maritime', 'conflict'], {
    name: 'UKMTO Maritime Warnings',
    category: 'early_warning',
    base_url: 'https://r.jina.ai/http://www.ukmto.org/recent-incidents',
    origin_key: 'ukmto',
    access_method: 'public_page',
    purposes: ['incident_reporting', 'notifications', 'map_updates', 'early_warning'],
    description: 'Official-origin UKMTO maritime warnings from the public Recent Incidents page. Updates are correlated by UKMTO warning number and vessel, while publication-to-platform latency is measured automatically.',
  }),
  usgs: WITH_SELECTED_HAZARD_COVERAGE('usgs', ['earthquake'], { name: 'USGS Earthquake Feed', category: 'early_warning', origin_key: 'usgs', access_method: 'official_api', base_url: 'https://earthquake.usgs.gov/fdsnws/event/1/query?format=geojson&minmagnitude=3&orderby=time&limit=25', purposes: ['incident_reporting', 'map_updates', 'early_warning'], description: 'Live earthquakes (M3+) — automatically scoped to the Operations Area. USGS open API, no key required.' }),
  emsc: WITH_SELECTED_HAZARD_COVERAGE('emsc', ['earthquake'], { name: 'EMSC Earthquakes', category: 'early_warning', origin_key: 'emsc', access_method: 'official_api', base_url: 'https://www.seismicportal.eu/fdsnws/event/1/query?format=json&limit=25&minmag=3', purposes: ['incident_reporting', 'map_updates'], description: 'EMSC live earthquakes (M3+) — automatically scoped to the Operations Area. Open FDSN API, no key required.' }),
  eonet: WITH_SELECTED_HAZARD_COVERAGE('nasa_eonet', ['earthquake', 'fire', 'flood', 'storm', 'sandstorm', 'heat', 'cold', 'volcano', 'drought', 'landslide', 'pollution'], { name: 'NASA EONET Natural Events', category: 'early_warning', origin_key: 'nasa_eonet', access_method: 'official_api', base_url: 'https://eonet.gsfc.nasa.gov/api/v3/events?status=open&limit=25', purposes: ['incident_reporting', 'map_updates', 'gis'], description: 'Open natural events tracked by NASA — automatically scoped to the Operations Area. No key required.' }),
  gdacs: WITH_SELECTED_HAZARD_COVERAGE('gdacs', ['earthquake', 'fire', 'flood', 'storm', 'volcano', 'tsunami', 'drought'], { name: 'GDACS Global Disaster Alerts', category: 'early_warning', origin_key: 'gdacs', access_method: 'official_api', base_url: 'https://www.gdacs.org/gdacsapi/api/events/geteventlist/MAP', purposes: ['incident_reporting', 'early_warning'], description: 'UN/EU Global Disaster Alert and Coordination System — floods, cyclones, earthquakes, volcanoes, droughts, tsunamis. No key required.' }),
  who: WITH_SELECTED_HAZARD_COVERAGE('who', ['disease', 'biological'], { name: 'WHO Disease Outbreak News', category: 'news', origin_key: 'who', access_method: 'official_api', base_url: 'https://www.who.int/api/news/diseaseoutbreaknews?%24orderby=PublicationDateAndTime%20desc&%24top=20&%24format=json', purposes: ['incident_reporting', 'early_warning'], description: 'Official WHO disease outbreak notifications — health emergencies worldwide. Open API, no key.' }),
  openmeteo_air: WITH_SELECTED_HAZARD_COVERAGE('openmeteo', ['sandstorm', 'pollution'], { name: 'Open-Meteo Air Quality', category: 'weather', origin_key: 'openmeteo', access_method: 'official_api', base_url: 'https://air-quality-api.open-meteo.com/v1/air-quality?latitude={OPS_LAT}&longitude={OPS_LNG}&current=pm10,pm2_5,dust', purposes: ['map_updates', 'early_warning'], description: 'Air quality and dust observations at the Operations Area centre. Observations may open or corroborate pollution, sandstorm and environmental hazard cases. Free, no key.' }),
  openmeteo: WITH_SELECTED_HAZARD_COVERAGE('openmeteo', ['storm', 'sandstorm', 'heat', 'cold'], { name: 'Open-Meteo Weather', category: 'weather', origin_key: 'openmeteo', access_method: 'official_api', base_url: 'https://api.open-meteo.com/v1/forecast?latitude={OPS_LAT}&longitude={OPS_LNG}&current_weather=true', purposes: ['map_updates', 'early_warning'], description: 'Weather observations at the Operations Area centre used to detect or corroborate weather-related hazard cases. Free, no key.' }),
  gdelt_chemical: GDELT_TEMPLATE('gdelt_chemical', 'Chemical / HazMat', 'chemical OR hazmat OR "gas leak" OR "toxic spill"', ['chemical']),
  gdelt_conflict: GDELT_TEMPLATE('gdelt_security', 'Conflict / Security', 'attack OR conflict OR missile OR airstrike OR terrorism OR unrest', ['conflict', 'terrorism']),
  gdelt_accident: GDELT_TEMPLATE('gdelt_accident', 'Accidents / Industrial', 'explosion OR "building collapse" OR "industrial accident" OR factory', ['accident']),
  gdelt_transport: GDELT_TEMPLATE('gdelt_transport', 'Transportation', '"traffic accident" OR crash OR derail OR pileup', ['transport']),
  gdelt_maritime: GDELT_TEMPLATE('gdelt_regional', 'Maritime', 'vessel OR ship OR capsize OR "oil spill" OR maritime', ['maritime']),
  gdelt_aviation: GDELT_TEMPLATE('gdelt_aviation', 'Aviation', '"plane crash" OR aircraft OR "emergency landing" OR aviation', ['aviation']),
  gdelt_power: GDELT_TEMPLATE('gdelt_power', 'Power Outages', '"power outage" OR blackout OR "grid failure" OR "power cut"', ['power']),
  gdelt_radiation: GDELT_TEMPLATE('gdelt_radiation', 'Radiation / Nuclear', 'nuclear OR radiation OR radioactive OR radiological', ['radiation']),
  gdelt_cyber: GDELT_TEMPLATE('gdelt_cyber', 'Cyber Incidents', 'cyberattack OR ransomware OR "data breach" OR ddos', ['cyber']),
};

// Source links for the preview modal: the original report, any links embedded
// in the event brief, a provider event page derived from the external id
// (USGS / EMSC), and the location on Google Maps.
export const eventSourceLinks = (event) => {
  const links = [];
  const seen = new Set();
  const add = (label, url) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    links.push({ label, url });
  };

  add('Original report', event?.source_url);

  // URLs embedded in the description ("Details: https://…")
  const descUrls = String(event?.description || '').match(/https?:\/\/[^\s)"']+/g) || [];
  descUrls.forEach((u) => {
    try {
      add(new URL(u).hostname.replace(/^www\./, ''), u.replace(/[.,]$/, ''));
    } catch (e) { /* malformed — skip */ }
  });

  // Provider event pages derivable from the external id
  const ext = String(event?.external_id || '');
  if (/^(us|nc|ak|ci|uu|uw|nn|hv|pr|se|mb|nm|ok|tx)[a-z0-9]{6,}$/i.test(ext)) {
    add('USGS event page', `https://earthquake.usgs.gov/earthquakes/eventpage/${ext}`);
  }
  if (/^\d{8}_\d{7}$/.test(ext)) {
    add('EMSC event page', `https://www.seismicportal.eu/eventdetails.html?unid=${ext}`);
  }

  if (event?.gps?.latitude != null && event?.gps?.longitude != null) {
    add('View location on Google Maps', `https://www.google.com/maps?q=${event.gps.latitude},${event.gps.longitude}`);
  }
  return links;
};

// Hazard dictionary for unified ForeSight hazard cases. Each entry carries:
// - keywords: matched against event title/description
// - feedHints: substrings matched against a connection's name/url/description
//   to decide whether the user HAS a feed covering this hazard
// - suggestions: open APIs worth connecting when coverage is missing
export const HAZARD_FILTERS = [
  { id: 'earthquake', label: 'Earthquakes', icon: '🌍', keywords: ['earthquake', 'quake', 'seismic', 'aftershock', 'tremor', 'magnitude'],
    feedHints: ['usgs', 'seismic', 'emsc', 'earthquake', 'gdacs'],
    suggestions: [{ name: 'USGS Earthquakes (open)', url: 'https://earthquake.usgs.gov/fdsnws/event/1/' }, { name: 'EMSC Seismic Portal (open)', url: 'https://www.seismicportal.eu' }] },
  { id: 'fire', label: 'Fires', icon: '🔥', keywords: ['fire', 'wildfire', 'blaze', 'hotspot', 'burning', 'smoke'],
    feedHints: ['firms', 'eonet', 'fire', 'gdacs'],
    suggestions: [{ name: 'NASA FIRMS fire hotspots (free key)', url: 'https://firms.modaps.eosdis.nasa.gov/api/' }, { name: 'NASA EONET (open)', url: 'https://eonet.gsfc.nasa.gov' }] },
  { id: 'flood', label: 'Floods', icon: '🌊', keywords: ['flood', 'inundat', 'heavy rain', 'monsoon', 'deluge', 'flash flood'],
    feedHints: ['gdacs', 'flood', 'reliefweb'],
    suggestions: [{ name: 'GDACS alerts (open)', url: 'https://www.gdacs.org' }, { name: 'Copernicus GloFAS flood forecasts', url: 'https://global-flood.emergency.copernicus.eu' }] },
  { id: 'storm', label: 'Storms', icon: '🌀', keywords: ['storm', 'cyclone', 'hurricane', 'typhoon', 'tornado', 'hail', 'thunder', 'wind warning', 'gale'],
    feedHints: ['gdacs', 'weather', 'nws', 'eonet', 'meteo'],
    suggestions: [{ name: 'GDACS tropical cyclones (open)', url: 'https://www.gdacs.org' }, { name: 'Open-Meteo weather (open)', url: 'https://open-meteo.com' }] },
  { id: 'sandstorm', label: 'Sand / Dust Storms', icon: '🌪️', keywords: ['sandstorm', 'dust storm', 'haboob', 'dust'],
    feedHints: ['meteo', 'dust', 'sand', 'ncm'],
    suggestions: [{ name: 'WMO dust forecasts (SDS-WAS)', url: 'https://sds-was.aemet.es' }, { name: 'Open-Meteo Air Quality (open)', url: 'https://open-meteo.com/en/docs/air-quality-api' }] },
  { id: 'heat', label: 'Extreme Heat', icon: '🌡️', keywords: ['heat', 'heatwave', 'extreme temperature'],
    feedHints: ['weather', 'meteo', 'nws'],
    suggestions: [{ name: 'Open-Meteo forecasts (open)', url: 'https://open-meteo.com' }] },
  { id: 'cold', label: 'Winter / Cold', icon: '❄️', keywords: ['snow', 'blizzard', 'frost', 'freezing', 'ice storm', 'cold wave'],
    feedHints: ['weather', 'meteo', 'nws'],
    suggestions: [{ name: 'Open-Meteo forecasts (open)', url: 'https://open-meteo.com' }] },
  { id: 'volcano', label: 'Volcanoes', icon: '🌋', keywords: ['volcan', 'eruption', 'lava', 'ash plume'],
    feedHints: ['gdacs', 'eonet', 'volcano'],
    suggestions: [{ name: 'Smithsonian Global Volcanism (open)', url: 'https://volcano.si.edu' }, { name: 'NASA EONET (open)', url: 'https://eonet.gsfc.nasa.gov' }] },
  { id: 'tsunami', label: 'Tsunamis', icon: '🌐', keywords: ['tsunami'],
    feedHints: ['gdacs', 'tsunami'],
    suggestions: [{ name: 'NOAA Tsunami warnings (open)', url: 'https://www.tsunami.gov' }, { name: 'GDACS (open)', url: 'https://www.gdacs.org' }] },
  { id: 'drought', label: 'Droughts', icon: '🏜️', keywords: ['drought', 'water shortage'],
    feedHints: ['gdacs', 'drought', 'reliefweb'],
    suggestions: [{ name: 'GDACS (open)', url: 'https://www.gdacs.org' }, { name: 'ReliefWeb (free appname)', url: 'https://apidoc.reliefweb.int' }] },
  { id: 'landslide', label: 'Landslides', icon: '⛰️', keywords: ['landslide', 'mudslide', 'avalanche', 'rockfall'],
    feedHints: ['gdacs', 'landslide', 'eonet', 'reliefweb'],
    suggestions: [{ name: 'NASA EONET (open)', url: 'https://eonet.gsfc.nasa.gov' }, { name: 'ReliefWeb (free appname)', url: 'https://apidoc.reliefweb.int' }] },
  { id: 'disease', label: 'Disease Outbreaks', icon: '🦠', keywords: ['outbreak', 'virus', 'disease', 'epidemic', 'pandemic', 'cholera', 'ebola', 'influenza', 'mers', 'nipah', 'hantavirus', 'measles', 'polio', 'dengue', 'malaria'],
    feedHints: ['who', 'outbreak', 'disease', 'health'],
    suggestions: [{ name: 'WHO Disease Outbreak News (open)', url: 'https://www.who.int/emergencies/disease-outbreak-news' }, { name: 'ProMED alerts', url: 'https://promedmail.org' }] },
  { id: 'biological', label: 'Biological Hazards', icon: '🧪', keywords: ['biological', 'biohazard', 'anthrax', 'contamination', 'toxin'],
    feedHints: ['who', 'biological', 'health'],
    suggestions: [{ name: 'WHO Disease Outbreak News (open)', url: 'https://www.who.int/emergencies/disease-outbreak-news' }] },
  { id: 'radiation', label: 'Radiation / Nuclear', icon: '☢️', keywords: ['radiation', 'radioactive', 'nuclear', 'radiological', 'fallout'],
    feedHints: ['iaea', 'nuclear', 'radiation'],
    suggestions: [{ name: 'IAEA incident reports', url: 'https://www.iaea.org/resources/databases/news' }, { name: 'EU radiological data (EURDEP)', url: 'https://remap.jrc.ec.europa.eu' }] },
  { id: 'chemical', label: 'Chemical / HazMat', icon: '☣️', keywords: ['chemical', 'hazmat', 'toxic', 'gas leak', 'spill', 'ammonia', 'chlorine'],
    feedHints: ['chemical', 'hazmat', 'gdelt', 'news'],
    suggestions: [{ name: 'GDELT news monitor with a chemical query (open)', url: 'https://api.gdeltproject.org/api/v2/doc/doc' }] },
  { id: 'conflict', label: 'Conflict / War', icon: '⚔️', keywords: ['war', 'conflict', 'attack', 'missile', 'airstrike', 'bomb', 'shooting', 'unrest', 'riot', 'clashes', 'insurgent', 'militant'],
    feedHints: ['gdelt', 'news', 'acled', 'conflict', 'mastodon', 'reliefweb'],
    suggestions: [{ name: 'ACLED conflict data (free key)', url: 'https://acleddata.com' }, { name: 'GDELT news monitor (open)', url: 'https://api.gdeltproject.org/api/v2/doc/doc' }] },
  { id: 'terrorism', label: 'Terrorism / Security', icon: '💣', keywords: ['terror', 'hostage', 'ied', 'suicide bomb', 'security incident'],
    feedHints: ['gdelt', 'news', 'acled'],
    suggestions: [{ name: 'GDELT news monitor (open)', url: 'https://api.gdeltproject.org/api/v2/doc/doc' }, { name: 'ACLED (free key)', url: 'https://acleddata.com' }] },
  { id: 'accident', label: 'Accidents / Industrial', icon: '🏭', keywords: ['accident', 'explosion', 'collapse', 'industrial', 'factory', 'mine'],
    feedHints: ['gdelt', 'news', 'mastodon'],
    suggestions: [{ name: 'GDELT news monitor (open)', url: 'https://api.gdeltproject.org/api/v2/doc/doc' }] },
  { id: 'transport', label: 'Transportation', icon: '🚗', keywords: ['crash', 'collision', 'derail', 'pileup', 'traffic accident'],
    feedHints: ['gdelt', 'news', 'traffic'],
    suggestions: [{ name: 'GDELT news monitor (open)', url: 'https://api.gdeltproject.org/api/v2/doc/doc' }] },
  { id: 'aviation', label: 'Aviation', icon: '✈️', keywords: ['plane crash', 'aircraft', 'aviation', 'emergency landing', 'airline'],
    feedHints: ['aviation', 'opensky', 'flight', 'gdelt'],
    suggestions: [{ name: 'OpenSky Network (open)', url: 'https://opensky-network.org' }, { name: 'Aviation Safety Network', url: 'https://asn.flightsafety.org' }] },
  { id: 'maritime', label: 'Maritime', icon: '🚢', keywords: ['ship', 'vessel', 'sinking', 'capsize', 'maritime', 'oil spill'],
    feedHints: ['ukmto', 'maritime', 'marine', 'vessel', 'gdelt'],
    suggestions: [{ name: 'UKMTO official warnings (open)', url: 'https://www.ukmto.org/ukmto-products/warnings' }, { name: 'GDELT news monitor (open)', url: 'https://api.gdeltproject.org/api/v2/doc/doc' }, { name: 'EMSA maritime safety', url: 'https://www.emsa.europa.eu' }] },
  { id: 'power', label: 'Power / Electrical', icon: '⚡', keywords: ['power outage', 'blackout', 'electrical', 'grid failure', 'power cut'],
    feedHints: ['power', 'grid', 'outage', 'gdelt'],
    suggestions: [{ name: 'GDELT news monitor with an outage query (open)', url: 'https://api.gdeltproject.org/api/v2/doc/doc' }] },
  { id: 'cyber', label: 'Cyber / Network', icon: '💻', keywords: ['cyber', 'ransomware', 'hack', 'data breach', 'ddos', 'malware', 'network outage'],
    feedHints: ['cyber', 'cisa', 'cert', 'security advisories'],
    suggestions: [{ name: 'CISA cybersecurity advisories (open)', url: 'https://www.cisa.gov/news-events/cybersecurity-advisories' }, { name: 'NVD vulnerability feed (open)', url: 'https://nvd.nist.gov/developers' }] },
  { id: 'pollution', label: 'Air Quality / Pollution', icon: '🌫️', keywords: ['air quality', 'pollution', 'smog', 'aqi'],
    feedHints: ['openaq', 'air quality', 'meteo'],
    suggestions: [{ name: 'OpenAQ air quality (open)', url: 'https://openaq.org' }, { name: 'Open-Meteo Air Quality (open)', url: 'https://open-meteo.com/en/docs/air-quality-api' }] },
];

// SuperConnect: which ready-made connection templates can close each hazard's
// coverage gap with one click. Hazards not listed here only get link
// suggestions (their sources need registration or have no open API).
export const HAZARD_SUPER_CONNECT = {
  earthquake: ['usgs', 'emsc'],
  fire: ['eonet', 'gdacs'],
  flood: ['gdacs'],
  storm: ['gdacs', 'openmeteo'],
  sandstorm: ['openmeteo_air'],
  heat: ['openmeteo'],
  cold: ['openmeteo'],
  volcano: ['gdacs', 'eonet'],
  tsunami: ['gdacs'],
  drought: ['gdacs'],
  landslide: ['gdacs', 'eonet'],
  disease: ['who'],
  biological: ['who'],
  radiation: ['gdelt_radiation'],
  chemical: ['gdelt_chemical'],
  conflict: ['gdelt_conflict'],
  terrorism: ['gdelt_conflict'],
  accident: ['gdelt_accident'],
  transport: ['gdelt_transport'],
  aviation: ['gdelt_aviation'],
  maritime: ['ukmto', 'gdelt_maritime'],
  power: ['gdelt_power'],
  cyber: ['gdelt_cyber'],
  pollution: ['openmeteo_air'],
};

// Pricing label for a curated suggestion, inferred from its name annotation.
export const suggestionPricing = (s) => {
  const n = (s?.name || '').toLowerCase();
  if (n.includes('(open)')) return { label: 'Free', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (n.includes('free key') || n.includes('free appname') || n.includes('registration')) return { label: 'Free · registration', cls: 'bg-sky-50 text-sky-700 border-sky-200' };
  return { label: 'Check site', cls: 'bg-gray-50 text-gray-500 border-gray-200' };
};

// Categories shown as chips by default — the dictionary button lets the user
// enable the rest (stored in localStorage).
export const DEFAULT_HAZARD_IDS = [
  'earthquake', 'fire', 'flood', 'storm', 'sandstorm', 'heat', 'volcano',
  'tsunami', 'drought', 'landslide', 'disease', 'conflict', 'accident',
];

// Which hazard categories does an event belong to? (an event can match several)
export const eventHazards = (event) => {
  const text = `${event?.title || ''} ${event?.description || ''}`.toLowerCase();
  return HAZARD_FILTERS.filter((h) => h.keywords.some((k) => text.includes(k))).map((h) => h.id);
};

// Does the user have any active connection covering a hazard? (monitoring
// feeds like air quality count as coverage even without event reporting)
export const hazardCoverage = (hazard, connections = []) =>
  connections.filter((connection) => {
    if (connection?.is_active === false || !hazard?.id) return false;
    const hazardId = String(hazard.id).trim().toLowerCase();
    const mode = String(connection?.hazard_coverage || '').trim().toLowerCase();
    if (mode === 'all') return true;
    const effectiveIds = Array.isArray(connection?.effective_hazard_ids)
      ? connection.effective_hazard_ids
      : [];
    const configuredIds = effectiveIds.length > 0
      ? effectiveIds
      : Array.isArray(connection?.hazard_ids)
        ? connection.hazard_ids
        : Array.isArray(connection?.hazards) ? connection.hazards : [];
    const normalizedIds = configuredIds
      .map((value) => String(value?.id || value || '').trim().toLowerCase())
      .filter(Boolean);
    if (mode === 'selected' || normalizedIds.length > 0) {
      return normalizedIds.includes(hazardId);
    }
    // A current API response explicitly marks unrelated legacy integrations
    // as ForeSight-disabled. Only responses from older servers fall back to
    // source-name/feed-hint inference.
    if (connection?.foresight_enabled === false) return false;
    const text = [
      connection?.name,
      connection?.base_url,
      connection?.description,
      connection?.origin_key,
    ].filter(Boolean).join(' ').toLowerCase();
    return (hazard.feedHints || []).some((hint) => text.includes(String(hint).toLowerCase()));
  });
